import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Outlet, Link } from 'react-router-dom';
import { useAuth } from '../shared/AuthProvider';
import { tenantApi } from '../services/tenantApi';

interface TenantContextType {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  tenantConfig: TenantConfig;
}

interface TenantConfig {
  branding?: {
    logo?: string;
    primaryColor?: string;
    favicon?: string;
  };
  features?: {
    teamsEnabled?: boolean;
    smsEnabled?: boolean;
    emailEnabled?: boolean;
    selfServe?: boolean;
  };
}

const TenantContext = React.createContext<TenantContextType | null>(null);

export const useTenant = () => {
  const context = React.useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantRoute');
  }
  return context;
};

export const TenantRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { isAuthenticated, isLoading, tenantId, hasPermission, tenantName: authTenantName } = useAuth();
  const navigate = useNavigate();
  const [tenantConfig] = useState<TenantConfig>({});
  const [resolvedTenantName, setResolvedTenantName] = useState<string>('');

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      // Redirect to home page where user can enter email and choose sign-in method
      navigate('/', { replace: true });
      return;
    }

    if (tenantId && hasPermission('ManageUsers')) {
      loadTenantConfig(tenantSlug!);
    }
  }, [isAuthenticated, isLoading, tenantId, tenantSlug, navigate, hasPermission]);

  useEffect(() => {
    setResolvedTenantName(authTenantName || '');
  }, [authTenantName]);

  const loadTenantConfig = async (slug: string) => {
    try {
      const settings = await tenantApi.getTenantSettings();
      setResolvedTenantName(settings.name || slug || authTenantName || '');
    } catch (error) {
      // Branding/config endpoint is optional for tenant rendering; avoid noisy hard failures.
      console.warn('Tenant settings unavailable for current user/session; using defaults.');
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading {tenantSlug}...</p>
      </div>
    );
  }

  if (!tenantId) {
    // While the redirect in useEffect navigates away, render a loading state
    // to avoid a flash of blank content
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Redirecting to sign in...</p>
      </div>
    );
  }

  return (
    <TenantContext.Provider
      value={{
        tenantId,
        tenantSlug: tenantSlug!,
        tenantName: resolvedTenantName,
        tenantConfig,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const TenantLayout: React.FC = () => {
  const { tenantConfig, tenantSlug } = useTenant();
  const { user, logout, tenantName } = useAuth();

  const primaryColor = tenantConfig.branding?.primaryColor || '#2f9e91';

  return (
    <div className="tenant-layout" style={{ '--brand-color': primaryColor } as React.CSSProperties}>
      <header className="tenant-header">
        <div className="header-left">
          {tenantConfig.branding?.logo ? (
            <img src={tenantConfig.branding.logo} alt="Logo" className="tenant-logo" />
          ) : (
            <span className="tenant-name">{tenantName || 'Tenant'}</span>
          )}
        </div>
        <nav className="header-nav">
          <Link to={`/${tenantSlug}/dashboard`}>Dashboard</Link>
          <Link to={`/${tenantSlug}/alerts`}>Alerts</Link>
          <Link to={`/${tenantSlug}/users`}>Users</Link>
          <Link to={`/${tenantSlug}/integrations`}>Integrations</Link>
          <Link to={`/${tenantSlug}/settings`}>Settings</Link>
        </nav>
        <div className="header-right">
          <span className="user-name">{user?.displayName}</span>
          <button onClick={logout} className="btn-logout">
            Logout
          </button>
        </div>
      </header>
      <main className="tenant-main">
        <Outlet />
      </main>
    </div>
  );
};

export default TenantRoute;
